/*
 * @providesModule HTMLText
 * 
 */

import React, {Component, ReactNode} from 'react';

import {parseFragment} from 'parse5';

import {
  Text,
  Linking,
} from 'react-native';

import {
  BLOCK_ELEMENTS,
  INLINE_ELEMENTS,
  DEFAULT_STYLES,
} from './constants';

function isHyperlink(tagName:string): boolean {
  return tagName === 'a';
}

function isText(tagName:string): boolean {
  return tagName === '#text';
}

function isBlockElement(tagName:string): boolean {
  return BLOCK_ELEMENTS.indexOf(tagName) != -1;
}

function isInlineElement(tagName:string): boolean {
  return INLINE_ELEMENTS.indexOf(tagName) != -1;
}

function styleForTag(tagName:string): Object {
  const style = (DEFAULT_STYLES as any)[tagName] ? (DEFAULT_STYLES as any)[tagName] : DEFAULT_STYLES['default'];
  return style;
}

function processNode(node:any, parentKey:any, options:{style: StyleSheet}): React.ReactNode | null {
  const nodeName = node.nodeName;

  if (isHyperlink(nodeName)) {
    const key = `${parentKey}_text`;
    const href = node.attrs[0];
    if (isInlineElement(nodeName)) {
      const key2 = `${parentKey}_${nodeName}`;
      const children: ReactNode[] = [];
      node.childNodes.forEach((child:any, index:number) => {
        if (isInlineElement(child.nodeName) || isText(child.nodeName)) {
          children.push(processNode(child, `${key2}_${index}`, options));
        } else {
          console.warn(`Inline element ${nodeName} can only have inline children, ${child} is invalid!`);
        }
      });
      return (
        <Text key={key}
              style={[options.style, styleForTag(nodeName)]}
              onPress={() => {
                Linking.canOpenURL(href.value).then((supported:boolean) => {
                  if (!supported) {
                    console.warn('Can\'t handle url: ' + href.value);
                  } else {
                    Linking.openURL(href.value);
                  }
                });
              }}>
          {children}
        </Text>
      );
    }
  }

  if (isText(nodeName)) {
    const key = `${parentKey}_text`;
    return (<Text key={key} >{node.value}</Text>);
  }

  if (isInlineElement(nodeName)) {
    const key = `${parentKey}_${nodeName}`;
    const children: ReactNode[] = [];
    node.childNodes.forEach((child:any, index:number) => {
      if (isInlineElement(child.nodeName) || isText(child.nodeName)) {
        children.push(processNode(child, `${key}_${index}`, options));
      } else {
        console.warn(`Inline element ${nodeName} can only have inline children, ${child} is invalid!`);
      }
    });
    return (
      <Text
        key={key}
        style={[styleForTag(nodeName), options.style]}>
        {children}
      </Text>
    );
  }

  if (isBlockElement(nodeName)) {
    const key = `${parentKey}_${nodeName}`;
    const children = [];
    let lastInlineNodes: ReactNode[] = [];

    node.childNodes.forEach((childNode:any, index:number) => {
      const child = processNode(childNode, `${key}_${index}`, options);
      if (isInlineElement(childNode.nodeName) || isText(childNode.nodeName)) {
        lastInlineNodes.push(child);

      } else if (isBlockElement(childNode.nodeName)) {
        if (lastInlineNodes.length > 0) {
          children.push(
            <Text
              key={`${key}_${index}_inline`}
              style={[styleForTag(nodeName), options.style]}>{lastInlineNodes}
            </Text>);
          lastInlineNodes = [];
        }
        children.push(child);
      }
    });

    if (lastInlineNodes.length > 0) {
      children.push(
        <Text key={`${key}_last_inline`} style={[styleForTag(nodeName), options.style]}>
          {lastInlineNodes}
        </Text>
      );
    }
    return (
      <Text key={key}>
        {children}
      </Text>
    );
  }

  console.warn(`unsupported node: ${nodeName}`);
  return null;
}

type IHTMLTextProps = {
  html: string,
  containerStyle?: any,
  style?: any,
  accessibilityLabel?: string,
  testID?: string,
};

export default class HtmlText extends Component<IHTMLTextProps> {

  parseHtml(html: string) {
    if (parseFragment) {
      const fragment = parseFragment(html);
      return fragment;
    } else {
      console.warn('parse5 is not available!');
      return null;
    }
  }

  render(): React.ReactNode {
    const html = this.props.html;
    const fragment = this.parseHtml(html);
    const rootKey = 'ht_';
    if (fragment) {
      const children: ReactNode[] = [];
      fragment.childNodes.forEach((node:any, index:number) => {
        children.push(processNode(node, `${rootKey}_${index}`, {style: this.props.style}));
      });
  
      const accessibilityProps = (this.props.accessibilityLabel || this.props.testID) ?
        {accessibilityLabel: this.props.accessibilityLabel, testID: this.props.testID, accessible: true} : {};
  
      return (
        <Text {...accessibilityProps} style={this.props.containerStyle}>
          {children}
        </Text>
      );
    } else {
      return null;
    }

  }
}
